import { createContext, useContext, ReactNode, useEffect, useMemo, useState } from 'react';
import { useQuery, ApolloClient } from '@apollo/client';
import { useLocale } from '../../../../../contexts/LocaleProvider';
import {
    GET_RESOURCE_LIBRARY_SITE_PAGE_ITEMS_QUERY,
    getResourceLibrarySitePageItemsQueryVariables,
} from '../queries/getResourceLibrarySitePageItemsQuery';
import { ResourceLibrarySitePageBaseItem } from '../types/resourceLibrarySitePageItems';
import { transformResourceLibrarySitePageItemsResponse } from '../tools/transformResourceLibrarySitePageItemsResponse';
import { GetResourceLibrarySitePageItemListingQuery } from '../schemas/generated-types/graphql';
import { useQueryFallbackResponse } from '../../../common/hooks/useQueryFallbackResponse';

interface ResourceLibrarySitePageItemsContextValue {
    items: ResourceLibrarySitePageBaseItem[];
    loading: boolean;
    error: any;
    refetch: () => void;
    initialize: () => void;
}

const ResourceLibrarySitePageItemsContext = createContext<
    ResourceLibrarySitePageItemsContextValue | undefined
>(undefined);

interface ResourceLibrarySitePageItemsProviderProps {
    children: ReactNode;
    apolloClient: ApolloClient<any>;
}

const ResourceLibrarySitePageItemsProvider = ({
    children,
    apolloClient,
}: ResourceLibrarySitePageItemsProviderProps) => {
    const { locale } = useLocale();
    const [initialized, setInitialized] = useState(false);

    const query = GET_RESOURCE_LIBRARY_SITE_PAGE_ITEMS_QUERY;
    const {
        loading: loadingQuery,
        error: queryError,
        data: queryData,
    } = useQuery<GetResourceLibrarySitePageItemListingQuery>(query, {
        variables: getResourceLibrarySitePageItemsQueryVariables(locale),
        client: apolloClient,
        errorPolicy: 'all',
        skip: !initialized,
    });

    const {
        fallbackData,
        loading: loadingFallback,
        error: fallbackError,
    } = useQueryFallbackResponse({ query, locale, initialized, queryError });

    const initialize = () => {
        if (!initialized) {
            setInitialized(true);
        }
    };

    const refetch = () => {
        if (initialized) {
            apolloClient.refetchQueries({ include: 'active' });
        } else {
            initialize();
        }
    };

    const items = useMemo(() => {
        const source = fallbackData ?? queryData;
        if (!source) {
            return [];
        }
        return transformResourceLibrarySitePageItemsResponse(source);
    }, [queryData, fallbackData]);

    const loading = loadingQuery || loadingFallback;
    const error = fallbackError || (queryError && !fallbackData ? queryError : null);

    return (
        <ResourceLibrarySitePageItemsContext.Provider
            value={{
                items,
                loading,
                error,
                refetch,
                initialize,
            }}
        >
            {children}
        </ResourceLibrarySitePageItemsContext.Provider>
    );
};

function useResourceLibrarySitePageItemsUnsafe() {
    const context = useContext(ResourceLibrarySitePageItemsContext);
    if (!context) {
        throw new Error(
            'useResourceLibrarySitePageItems must be used within a ResourceLibrarySitePageItemsProvider'
        );
    }
    return context;
}

function useResourceLibrarySitePageItems() {
    const context = useResourceLibrarySitePageItemsUnsafe();
    useEffect(() => {
        context.initialize();
    }, [context]);
    return context;
}

export {
    ResourceLibrarySitePageItemsProvider,
    useResourceLibrarySitePageItems,
    useResourceLibrarySitePageItemsUnsafe,
};
export type { ResourceLibrarySitePageItemsContextValue };
